import { Box, Fade, Link } from '@chakra-ui/react';
import NextImage from 'next/image';
import NextLink from 'next/link';
import type { ReactNode } from 'react';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import {
  BANNER_CROSSFADE_DURATION_MS,
  useCreativeCrossfadeLayer,
} from '../hooks/useCreativeCrossfadeLayer';
import type { CreativeLayer } from '../hooks/useCreativeCrossfadeLayer';
import type { BannerCreative } from '../server/libs/siteSettings';
import { generateS3DownloadUrl } from '../server/libs/siteSettings';

const FADE_DURATION_SEC = BANNER_CROSSFADE_DURATION_MS / 1000;

function useBannerCreativeLayer(
  creative: BannerCreative,
  imgS3Key?: string | null,
) {
  const imageSrc = generateS3DownloadUrl(imgS3Key);
  return useCreativeCrossfadeLayer(creative, imageSrc);
}

function CreativeBannerLink({
  creative,
  children,
}: {
  creative: BannerCreative;
  children: ReactNode;
}) {
  return (
    <NextLink href={creative.linkUrl} passHref>
      <Link
        _hover={{ opacity: '0.8' }}
        isExternal={creative.isExternalLink ?? false}
        onClick={() =>
          sendAnalyticsEvent('slide', 'click', `${creative.name}_banner`)
        }
      >
        {children}
      </Link>
    </NextLink>
  );
}

function PreviousCreativeLayerImage({ layer }: { layer: CreativeLayer }) {
  return (
    <Box pos="absolute" inset={0} pointerEvents="none">
      <NextImage
        src={layer.imageSrc}
        alt={layer.creative.alt}
        layout="fill"
        objectFit="contain"
      />
    </Box>
  );
}

function ActiveCreativeLayerFade({
  activeLayer,
  previousLayer,
  isFading,
}: {
  activeLayer: CreativeLayer;
  previousLayer: CreativeLayer | null;
  isFading: boolean;
}) {
  return (
    <Fade
      in={previousLayer ? isFading : true}
      transition={{
        enter: { duration: previousLayer ? FADE_DURATION_SEC : 0 },
        exit: { duration: 0 },
      }}
    >
      <Box pos="absolute" inset={0}>
        <NextImage
          src={activeLayer.imageSrc}
          alt={activeLayer.creative.alt}
          layout="fill"
          objectFit="contain"
        />
      </Box>
    </Fade>
  );
}

export function PcCreativeBannerCrossfade({
  creative,
}: {
  creative: BannerCreative;
}) {
  const { activeLayer, previousLayer, isFading } = useBannerCreativeLayer(
    creative,
    creative.images.pc.imgS3Key,
  );

  return (
    <Box
      textAlign="center"
      w="full"
      maxW="470"
      aspectRatio={1300 / 720}
      pos="relative"
      overflow="hidden"
    >
      {previousLayer && <PreviousCreativeLayerImage layer={previousLayer} />}
      <CreativeBannerLink creative={activeLayer.creative}>
        <ActiveCreativeLayerFade
          activeLayer={activeLayer}
          previousLayer={previousLayer}
          isFading={isFading}
        />
      </CreativeBannerLink>
    </Box>
  );
}

export function MobileBannerCreative({
  creative,
}: {
  creative: BannerCreative;
}) {
  const { activeLayer, previousLayer, isFading } = useBannerCreativeLayer(
    creative,
    creative.images.mobile.imgS3Key,
  );

  return (
    <Box position="fixed" bottom={0} zIndex="900">
      <Box rounded={['lg', 'xl', null]}>
        {previousLayer && <PreviousCreativeLayerImage layer={previousLayer} />}
        <CreativeBannerLink creative={activeLayer.creative}>
          <Box
            position="relative"
            width="100vw"
            maxWidth="1200px"
            aspectRatio={1200 / 240}
            overflow="hidden"
          >
            <ActiveCreativeLayerFade
              activeLayer={activeLayer}
              previousLayer={previousLayer}
              isFading={isFading}
            />
          </Box>
        </CreativeBannerLink>
      </Box>
    </Box>
  );
}
